import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import { masterHandler, billHandler } from "../../redux/handlers";

const selectStyles = {
  ".MuiInputBase-input": {
    display: "flex",
    alignItems: "center",
    padding: "8px 14px",
    color: "#061126",
  },
  fieldset: {
    border: 0,
    background: "#fff",
    borderRadius: "8px",
    filter: "drop-shadow(rgba(0,0,0,.14) 0px 2px 4px)",
    color: "#061126",
  },
  ".MuiSvgIcon-root": { zIndex: 9 },
  ".MuiSelect-select": { zIndex: 10, height: "32.4px" },
};

export default function StockConfirmationVoid({
  setStep,
  modalOpenObj,
  modalSuccessOpenObj,
  billdata,
  businessSlug,
  returnProduct,
  setReturnProduct,
  onVoidSuccess,
  voidData,
  refreshBillData,
  verifiedPin,
}) {
  const dispatch = useDispatch();
  const paymentMethods = useSelector((s) => s.master.paymentMethods);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refundMethod, setRefundMethod] = useState("");

  useEffect(() => {
    dispatch(masterHandler.getPaymentMethods());
  }, [dispatch]);

  const refundOptions = useMemo(
    () =>
      (paymentMethods || []).filter((m) =>
        ["CASH", "CREDIT_CARD", "BANK_TRANSFER"].includes(m.code)
      ),
    [paymentMethods]
  );

  useEffect(() => {
    if (!refundMethod && refundOptions.length) {
      const cash = refundOptions.find((m) => m.code === "CASH");
      setRefundMethod(String(cash ? cash.id : refundOptions[0].id));
    }
  }, [refundOptions, refundMethod]);

  const totalRefundAmount = useMemo(() => {
    const n = (v) => (v != null ? Number(v) : NaN);
    const cands = [
      n(billdata?.totalRefund),
      n(billdata?.grandTotal),
      n(billdata?.totalPriceNet),
      n(billdata?.billTotalPaid),
      n(billdata?.totalPrice),
      n(billdata?.final_amount),
      n(billdata?.grand_total),
      n(billdata?.orderPrice?.net),
    ].filter((x) => !Number.isNaN(x));
    return cands.length ? cands[0] : 0;
  }, [billdata]);

  const handleSubmit = async () => {
    if (!returnProduct.stock || !refundMethod || !voidData?.reason_id) return;

    try {
      setIsSubmitting(true);

      const voidForm = {
        remark: voidData.note || voidData.reason_label || null,
        bill_void_remark_id: voidData.reason_id || 0,
        void_payment_method_id: parseInt(refundMethod),
        void_manager_pin: verifiedPin || null,
      };

      const voidResponse = await dispatch(
        billHandler.voidBill(voidForm, businessSlug, billdata.billID)
      );

      if (refreshBillData) {
        await refreshBillData();
      }

      modalOpenObj.setModalOpen(false);

      if (onVoidSuccess) {
        onVoidSuccess(voidResponse);
      }

      if (modalSuccessOpenObj?.setModalOpen) {
        modalSuccessOpenObj.setModalOpen(true);
      }
    } catch (err) {
      console.error("Error void bill:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    modalOpenObj.setModalOpen(false);
  };

  return (
    <Modal
      aria-labelledby="stock-confirmation-void-title"
      open={modalOpenObj.modalOpen}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={modalOpenObj.modalOpen}>
        <div className="bg-white w-[min(50vw,600px)] px-6 py-4 absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-[min(1.46vw,15px)] text-16 max-h-[90vh] overflow-auto">
          <div className="text-center py-2">
            <div
              id="stock-confirmation-void-title"
              className="text-[24px] font-[700] text-[#FF0000] mb-6"
            >
              สรุปการ VOID
            </div>

            <hr />

            <div className="my-6">
              <div className="text-[18px] text-[#061126] mb-4">
                คุณต้องการคืนสต็อกสินค้าใช่หรือไม่{" "}
                <span className="text-red-500">*</span>
              </div>
              <div className="flex justify-center gap-8 mb-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="stock"
                    value="0"
                    checked={returnProduct.stock === "0"}
                    onChange={(e) =>
                      setReturnProduct((p) => ({ ...p, stock: e.target.value }))
                    }
                    className="w-5 h-5 mr-3"
                    disabled={isSubmitting}
                  />
                  <span className="text-[16px]">ไม่คืนสต็อกสินค้า</span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="stock"
                    value="1"
                    checked={returnProduct.stock === "1"}
                    onChange={(e) =>
                      setReturnProduct((p) => ({ ...p, stock: e.target.value }))
                    }
                    className="w-5 h-5 mr-3"
                    disabled={isSubmitting}
                  />
                  <span className="text-[16px]">คืนสต็อกสินค้า</span>
                </label>
              </div>
            </div>

            <div className="mb-6">
              <div className="text-[18px] text-[#061126] mb-4">
                ช่องทางการคืนเงิน <span className="text-red-500">*</span>
              </div>
              <FormControl fullWidth>
                <Select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(String(e.target.value))}
                  sx={selectStyles}
                  displayEmpty
                  disabled={true}
                >
                  <MenuItem value="" disabled>
                    <span style={{ color: "#999" }}>
                      เลือกช่องทางการคืนเงิน
                    </span>
                  </MenuItem>
                  {refundOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      <ListItemText primary={opt.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="bg-[#FFC2C2] border border-red-200 rounded-lg p-2 mb-6">
              <div className="text-[18px] text-[#FF0000] font-semibold mb-2">
                มูลค่าที่ต้องคืน
              </div>
              <div className="text-[32px] text-[#FF0000] font-bold">
                {totalRefundAmount.toFixed(2)} ฿
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={handleClose}
                className="px-8 py-2 bg-red-500 text-white text-[18px] font-semibold rounded-lg hover:bg-red-600 transition-colors"
                disabled={isSubmitting}
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSubmit}
                className="px-8 py-2 bg-green-500 text-white text-[18px] font-semibold rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                disabled={
                  isSubmitting ||
                  !returnProduct.stock ||
                  !refundMethod ||
                  !voidData?.reason_id
                }
              >
                {isSubmitting ? "กำลังดำเนินการ..." : "ยืนยัน"}
              </button>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}